import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Button,
  Typography,
  Alert,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { PhotoCamera, Delete } from '@mui/icons-material';
import { User } from '../../types/user';
import { profileService } from '../../services/profileService';
import { CONFIG } from '../../config/constants';

interface AvatarUploadProps {
  user: User;
  onAvatarUpdated: (updatedUser: User) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({ user, onAvatarUpdated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Vui lòng chọn file hình ảnh');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Kích thước file không được vượt quá 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload immediately
    handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await profileService.uploadAvatar(file);
      
      // Get updated user profile
      const updatedProfile = await profileService.getCurrentProfile();
      onAvatarUpdated(updatedProfile);
      
      setSuccess('Cập nhật avatar thành công!');
      setAvatarPreview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi upload avatar');
      setAvatarPreview(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await profileService.deleteAvatar();
      
      // Get updated user profile
      const updatedProfile = await profileService.getCurrentProfile();
      onAvatarUpdated(updatedProfile);
      
      setSuccess('Xóa avatar thành công!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi xóa avatar');
    } finally {
      setIsLoading(false);
    }
  };

  const getAvatarSrc = () => {
    if (avatarPreview) return avatarPreview;
    if (user.avatar) {
      // If avatar path starts with 'http', use as is, otherwise prepend base URL
      return user.avatar.startsWith('http') 
        ? user.avatar 
        : `${CONFIG.API.STORAGE_URL}/${user.avatar}`;
    }
    return '';
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Ảnh đại diện
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={getAvatarSrc()}
              sx={{ width: 120, height: 120 }}
            >
              {user.name?.charAt(0).toUpperCase()}
            </Avatar>
            
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '50%',
                }}
              >
                <CircularProgress size={40} sx={{ color: 'white' }} />
              </Box>
            )}
          </Box>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileSelect}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<PhotoCamera />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
            >
              {user.avatar ? 'Thay đổi' : 'Chọn ảnh'}
            </Button>

            {user.avatar && (
              <IconButton
                color="error"
                onClick={handleDelete}
                disabled={isLoading}
                title="Xóa avatar"
              >
                <Delete />
              </IconButton>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            Chấp nhận: JPG, PNG, GIF. Tối đa: 2MB
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}; 